import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { PENDING_PURCHASE_KEY } from "@/pages/ResumePurchasePage";

type Phase = "credentials" | "otp";

export function LoginPage() {
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [phase, setPhase] = React.useState<Phase>("credentials");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [rememberDevice, setRememberDevice] = React.useState(false);
  const [otpCode, setOtpCode] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [otpError, setOtpError] = React.useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";

  function continueAfterSignIn() {
    // If the user had a plan/domain in progress before signing up (e.g. a
    // brand-new customer who detoured through sign-up + email verification),
    // resume that purchase instead of dumping them on an empty dashboard.
    const hasPending = !!localStorage.getItem(PENDING_PURCHASE_KEY);
    navigate(hasPending ? "/buy/resume" : from, { replace: true });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password, rememberDevice);
      if (result.requires2FA) {
        // Password was right, but this account has 2FA on — pause here
        // instead of navigating away; the backend only issued a short-lived
        // temp_token, not a real session yet.
        setPhase("otp");
        return;
      }
      continueAfterSignIn();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Couldn't sign you in. Try again.";
      toast({ title: "Sign-in failed", description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitOtp(e: React.FormEvent) {
    e.preventDefault();
    setOtpError(null);
    if (submitting) return;
    setSubmitting(true);
    try {
      await verifyTwoFactor(otpCode.trim());
      continueAfterSignIn();
    } catch (err) {
      setOtpError(
        err instanceof ApiError ? err.message : "That code didn't work. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === "otp") {
    return (
      <AuthLayout
        eyebrow="One more step"
        title="Enter your 2FA code"
        sub="Open your authenticator app and enter the 6-digit code for HosterDash."
      >
        <Card className="border-brand-border shadow-brand-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Two-factor authentication
            </CardTitle>
            <CardDescription>Signing in as {email}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmitOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">6-digit code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  required
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="text-center text-lg tracking-[0.5em]"
                />
              </div>
              {otpError && <p className="text-sm font-medium text-destructive">{otpError}</p>}
              <Button
                type="submit"
                disabled={submitting || otpCode.trim().length < 6}
                className="w-full"
                size="lg"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Verify and sign in
              </Button>
              <button
                type="button"
                className="w-full text-center text-xs font-medium text-muted-foreground hover:text-primary hover:underline"
                onClick={() => {
                  setPhase("credentials");
                  setOtpCode("");
                  setOtpError(null);
                }}
              >
                Use a different account
              </button>
            </form>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="Welcome back"
      title="Sign in to HosterDash"
      sub="Manage your domains, mailboxes and hosting in one place."
    >
      <Card className="border-brand-border shadow-brand-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl sm:text-2xl">Sign in</CardTitle>
          <CardDescription>Use the email you bought your plan with.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                checked={rememberDevice}
                onChange={e => setRememberDevice(e.target.checked)}
              />
              Remember this device
            </label>
            <Button type="submit" disabled={submitting} className="w-full" size="lg">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        New to HosterDash?{" "}
        <Link to="/register" className="font-medium text-primary hover:underline">
          Create an account
        </Link>
      </p>
      <p className="mt-2 text-center text-xs text-muted-foreground">
        Just looking?{" "}
        <Link to="/plans" className="font-medium text-primary hover:underline">
          See plans &amp; pricing
        </Link>
      </p>
    </AuthLayout>
  );
}

interface AuthLayoutProps {
  eyebrow: string;
  title: string;
  sub: string;
  children: React.ReactNode;
}

/**
 * Shared layout used by both /login and /register. Wraps the form card
 * in the same blue-tinted gradient background the landing pages use,
 * so the marketing → app handoff feels seamless.
 */
function AuthLayout({ eyebrow, title, sub, children }: AuthLayoutProps) {
  return (
    <div className="brand-gradient-bg brand-radial-halo relative min-h-[100dvh] overflow-hidden">
      <div className="relative z-10 mx-auto flex w-full max-w-md flex-col items-center px-4 py-10 sm:py-16">
        <Link to="/" className="mb-8 flex items-center">
          <BrandLogo className="h-9 w-auto" />
        </Link>

        <span className="brand-pill-recommended mb-4">{eyebrow}</span>
        <h1 className="text-center font-display text-2xl font-bold tracking-tight sm:text-3xl">
          {title}
        </h1>
        <p className="mt-2 max-w-sm text-center text-sm text-muted-foreground">{sub}</p>

        <div className="mt-7 w-full">{children}</div>
      </div>
    </div>
  );
}
